import { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import type {
  BedTimeseriesEntry,
  BedWateringAutomation,
} from '../../../types/simulation';
import { useTranslation } from 'react-i18next';
import { BED_METRICS, metricLabel, metricUnit, type BedMetricKey } from '../constants';

interface UseBedEnvironmentChartOptions {
  timeSeries: BedTimeseriesEntry[];
  selectedMetric: BedMetricKey;
  wateringAutomation?: BedWateringAutomation;
}

export const useBedEnvironmentChart = ({
  timeSeries,
  selectedMetric,
  wateringAutomation,
}: UseBedEnvironmentChartOptions): EChartsOption => {
  const { t } = useTranslation('beds');
  return useMemo(() => {
    const metricDef = BED_METRICS.find(m => m.key === selectedMetric);
    if (!metricDef || timeSeries.length === 0) {
      return {
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: { text: t('common:states.noData'), fontSize: 14, fill: '#999' },
        },
      };
    }

    const label = metricLabel(metricDef.key);
    const unit = metricUnit(metricDef);
    const dates = timeSeries.map(p => p.date);
    const values = timeSeries.map(p => (p as unknown as Record<string, number | undefined>)[selectedMetric] ?? null);
    const isMoisture = selectedMetric === 'moisturePct';
    const hasWateringAutomationHistory = (wateringAutomation?.events.length ?? 0) > 0;
    const guide = isMoisture && hasWateringAutomationHistory
      ? wateringAutomation?.guide
      : undefined;
    const automationLines = isMoisture
      ? (wateringAutomation?.events ?? []).map(event => ({
          name: event.type === 'watering_on' ? t('chart.autoWateringOn') : t('chart.autoWateringOff'),
          xAxis: event.date,
          lineStyle: {
            color: event.type === 'watering_on' ? '#1677ff' : '#8c8c8c',
            type: 'dashed' as const,
            width: 1,
          },
          label: {
            show: true,
            formatter: event.type === 'watering_on' ? t('chart.autoWateringOnShort') : t('chart.autoWateringOffShort'),
          },
        }))
      : [];
    const guideLines = guide
      ? [
          {
            name: t('chart.autoWateringThreshold'),
            yAxis: guide.activationPct,
            lineStyle: { color: '#fa8c16', type: 'dashed' as const, width: 1 },
            label: { formatter: t('chart.thresholdLabel', { pct: guide.activationPct.toFixed(0) }) },
          },
          {
            name: t('chart.autoWateringTarget'),
            yAxis: guide.targetPct,
            lineStyle: { color: '#52c41a', type: 'dotted' as const, width: 1 },
            label: { formatter: t('chart.targetLabel', { pct: guide.targetPct.toFixed(0) }) },
          },
        ]
      : [];
    const percentAxis = selectedMetric === 'moisturePct'
      || selectedMetric === 'nitrogen'
      || selectedMetric === 'phosphorus'
      || selectedMetric === 'potassium';

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const p = (params as Array<{ axisValueLabel: string; data: number | null; marker: string }>)[0];
          if (!p || p.data == null) return '';
          return `${p.axisValueLabel}<br/>${p.marker} ${label}: <b>${p.data.toFixed(1)}</b> ${unit}`;
        },
      },
      grid: { left: 50, right: 20, top: 30, bottom: 50 },
      xAxis: {
        type: 'category',
        data: dates,
        axisLabel: { rotate: 45, fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        name: unit || label,
        nameLocation: 'middle',
        nameGap: 35,
        ...(percentAxis ? { min: 0, max: 100 } : {}),
        ...(selectedMetric === 'pH' ? { min: 0, max: 14 } : {}),
        ...(!percentAxis && selectedMetric !== 'pH' ? { scale: true } : {}),
      },
      dataZoom: [
        { type: 'inside', start: 0, end: 100 },
        { type: 'slider', start: 0, end: 100, height: 20, bottom: 5 },
      ],
      series: [
        {
          type: 'line',
          data: values,
          smooth: false,
          symbol: 'none',
          lineStyle: { color: metricDef.color, width: 2 },
          areaStyle: { color: metricDef.color, opacity: 0.08 },
          markLine: isMoisture && (automationLines.length > 0 || guideLines.length > 0)
            ? {
                silent: true,
                symbol: ['none', 'none'],
                data: [...automationLines, ...guideLines],
              }
            : undefined,
          markArea: guide
            ? {
                silent: true,
                itemStyle: { color: '#52c41a', opacity: 0.08 },
                data: [[
                  { name: t('chart.comfort'), yAxis: guide.comfortMinPct },
                  { yAxis: guide.comfortMaxPct },
                ]],
              }
            : undefined,
        },
      ],
    };
  }, [timeSeries, selectedMetric, wateringAutomation, t]);
};
